import React, { useState, useRef } from 'react';
import { Upload } from 'lucide-react';
import ReCAPTCHA from 'react-google-recaptcha';
import SuccessModal from './SuccessModal';
import { RECAPTCHA_SITE_KEY } from '../config';

const CareersPage = () => {
    const form = useRef();
    const recaptchaRef = useRef();
    const [status, setStatus] = useState('');
    const [resumeFileName, setResumeFileName] = useState('');
    const [coverLetterFileName, setCoverLetterFileName] = useState('');
    const [captchaToken, setCaptchaToken] = useState(null);

    // Server upload endpoint - update this when you deploy
    const UPLOAD_ENDPOINT = '/api/upload-resume.php';

    const handleFileChange = (e, setFileName) => {
        const file = e.target.files[0];
        if (file) {
            setFileName(file.name);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Check if resume is uploaded (mandatory)
        const resumeInput = form.current.elements['resume'];
        if (!resumeInput.files || resumeInput.files.length === 0) {
            alert('Please upload your resume.');
            return;
        }

        // Check if work rights checkbox is checked
        const confirmCheckbox = form.current.elements['confirm_work_rights'];
        if (!confirmCheckbox.checked) {
            alert('Please confirm that you have the right to work in Australia and can meet screening requirements.');
            return;
        }

        // Check reCAPTCHA
        if (!captchaToken) {
            alert('Please complete the reCAPTCHA verification.');
            return;
        }

        setStatus('sending');

        // Create FormData and send to PHP backend
        const formData = new FormData(form.current);

        try {
            const response = await fetch(UPLOAD_ENDPOINT, {
                method: 'POST',
                body: formData,
            });

            const data = await response.json();

            if (response.ok && data.success) {
                setStatus('success');
                form.current.reset();
                setResumeFileName('');
                setCoverLetterFileName('');
                setCaptchaToken(null);
                recaptchaRef.current?.reset();
            } else {
                console.error('Upload error:', data.error);
                setStatus('error');
            }
        } catch (error) {
            console.error('Network error:', error);
            setStatus('error');
        }
    };

    const inputStyle = "w-full bg-gray-100 border border-gray-200 rounded-lg px-4 py-3 text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#008080] focus:border-transparent transition-colors";
    const labelStyle = "block text-gray-600 font-medium mb-2 text-left";

    const rolesData = {
        'Direct Support & Frontline': [
            'Mental Health Support Worker',
            'Disability Support Worker',
            'Community Support Worker',
            'Personal Care Assistant',
            'In-Home Support Worker'
        ],
        'Administration & Operations': [
            'Human Resources Coordinator',
            'Marketing Coordinator',
            'Finance Officer',
            'Operations Assistant',
            'Receptionist / Admin Support'
        ],
        'Coordination & Professional': [
            'Support Coordinator',
            'Case Manager',
            'Team Leader',
            'Service Coordinator',
            'Plan Manager'
        ],
        'Clinical & Specialist': [
            'Registered Nurse'
        ]
    };

    const requirements = [
        'NDIS Worker Screening Check (or willingness to obtain)',
        'National Police Check',
        'Working with Children Check (WWCC)',
        'Current First Aid & CPR Certificate',
        'Australian work rights (citizen, permanent resident, or valid visa)',
        'Current driver\'s licence and reliable vehicle (for most roles)'
    ];

    return (
        <div className="bg-white">
            {status === 'success' && (
                <SuccessModal
                    onClose={() => setStatus('')}
                    title="Application Submitted!"
                    message="Thank you for your interest in joining our team."
                    subMessage="We will review your application and get back to you soon."
                />
            )}

            {/* Hero Section */}
            <section className="py-16 px-6 md:px-12 max-w-6xl mx-auto">
                <h1 className="text-[#D99C3B] font-serif text-5xl md:text-6xl mb-6">Careers</h1>
                <p className="text-gray-600 text-lg md:text-xl leading-relaxed max-w-4xl">
                    Community Plus Care Services is a registered NDIS provider committed to supporting people
                    with disabilities to live their best lives. We are always looking for passionate, dedicated
                    individuals to join our growing team across a variety of roles.
                </p>
            </section>

            {/* Why Work With Us */}
            <section className="py-12 px-6 md:px-12 max-w-6xl mx-auto">
                <h2 className="text-[#D99C3B] font-serif text-3xl md:text-4xl mb-8">Why Work With Us</h2>
                <ul className="space-y-4 text-gray-700 text-lg">
                    <li className="flex items-start">
                        <span className="text-[#008080] mr-3 mt-1">•</span>
                        <span><strong>Flexible Work Arrangements</strong> — Choose shifts that suit your lifestyle, including part-time and casual options</span>
                    </li>
                    <li className="flex items-start">
                        <span className="text-[#008080] mr-3 mt-1">•</span>
                        <span><strong>Supportive Team Culture</strong> — Work alongside a passionate, inclusive team that values collaboration</span>
                    </li>
                    <li className="flex items-start">
                        <span className="text-[#008080] mr-3 mt-1">•</span>
                        <span><strong>Competitive Pay</strong> — Remuneration in line with the SCHADS Award, with penalty rates where applicable</span>
                    </li>
                    <li className="flex items-start">
                        <span className="text-[#008080] mr-3 mt-1">•</span>
                        <span><strong>Training & Development</strong> — Ongoing professional development opportunities and career pathways</span>
                    </li>
                </ul>
            </section>

            {/* Roles You Can Apply For */}
            <section className="py-12 px-6 md:px-12 max-w-6xl mx-auto">
                <h2 className="text-[#D99C3B] font-serif text-3xl md:text-4xl mb-8">Roles You Can Apply For</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                    {Object.entries(rolesData).map(([category, roles]) => (
                        <div key={category} className="bg-gray-50 rounded-xl p-6">
                            <h3 className="text-[#008080] font-semibold text-lg mb-4">{category}</h3>
                            <ul className="space-y-2 text-gray-600">
                                {roles.map((role, index) => (
                                    <li key={index} className="text-sm">• {role}</li>
                                ))}
                            </ul>
                        </div>
                    ))}
                </div>
            </section>

            {/* Disclaimer */}
            <section className="px-6 md:px-12 max-w-6xl mx-auto pb-8">
                <div className="bg-gray-100 rounded-lg p-4 border-l-4 border-[#008080]">
                    <h3 className="text-[#008080] font-semibold text-sm mb-2">Disclaimer</h3>
                    <p className="text-gray-600 text-sm">
                        Roles, requirements, and availability may vary based on participant needs, funding, and location.
                        Community Plus Care Services reserves the right to update role requirements in line with NDIS Practice
                        Standards and regulatory changes.
                    </p>
                </div>
            </section>

            {/* Minimum Requirements */}
            <section className="py-12 px-6 md:px-12 max-w-6xl mx-auto">
                <h2 className="text-[#D99C3B] font-serif text-3xl md:text-4xl mb-8">Minimum Requirements</h2>
                <p className="text-gray-600 mb-6">Requirements may vary depending on the role. Common requirements include:</p>
                <ul className="space-y-3 text-gray-700">
                    {requirements.map((req, index) => (
                        <li key={index} className="flex items-start">
                            <span className="text-[#008080] mr-3">✓</span>
                            <span>{req}</span>
                        </li>
                    ))}
                </ul>
            </section>

            {/* Application Form */}
            <section className="py-16 px-6 md:px-12 bg-[#F4FAFB]">
                <div className="max-w-3xl mx-auto">
                    <h2 className="text-[#D99C3B] font-serif text-3xl md:text-4xl mb-8 text-center">Work with us</h2>

                    <form ref={form} onSubmit={handleSubmit} className="space-y-6">
                        {/* Name */}
                        <div>
                            <label className={labelStyle}>Name <span className="text-red-500">*</span></label>
                            <input
                                type="text"
                                name="applicant_name"
                                className={inputStyle}
                                required
                            />
                        </div>

                        {/* Email */}
                        <div>
                            <label className={labelStyle}>Email <span className="text-red-500">*</span></label>
                            <input
                                type="email"
                                name="applicant_email"
                                className={inputStyle}
                                required
                            />
                        </div>

                        {/* Phone */}
                        <div>
                            <label className={labelStyle}>Phone <span className="text-red-500">*</span></label>
                            <input
                                type="tel"
                                name="applicant_phone"
                                className={inputStyle}
                                required
                                pattern="[0-9]{10}"
                                maxLength={10}
                                title="Phone number must be exactly 10 digits"
                                placeholder="e.g., 0412345678"
                            />
                        </div>

                        {/* Qualifications */}
                        <div>
                            <label className={labelStyle}>Qualifications <span className="text-red-500">*</span></label>
                            <input
                                type="text"
                                name="qualifications"
                                className={inputStyle}
                                placeholder="e.g., Certificate III in Individual Support"
                                required
                            />
                        </div>

                        {/* Years of Experience */}
                        <div>
                            <label className={labelStyle}>Years of Experience <span className="text-red-500">*</span></label>
                            <input
                                type="text"
                                name="experience"
                                className={inputStyle}
                                placeholder="e.g., 3 years"
                                required
                            />
                        </div>

                        {/* Applying For */}
                        <div>
                            <label className={labelStyle}>Applying For <span className="text-red-500">*</span></label>
                            <select
                                name="applying_for"
                                className={inputStyle}
                                required
                                defaultValue=""
                            >
                                <option value="" disabled>Select a role...</option>
                                {Object.entries(rolesData).map(([category, roles]) => (
                                    <optgroup key={category} label={category}>
                                        {roles.map((role, index) => (
                                            <option key={index} value={role}>{role}</option>
                                        ))}
                                    </optgroup>
                                ))}
                            </select>
                        </div>

                        {/* Current Employer */}
                        <div>
                            <label className={labelStyle}>Current Employer</label>
                            <input
                                type="text"
                                name="current_employer"
                                className={inputStyle}
                                placeholder="Optional"
                            />
                        </div>

                        {/* Resume Upload */}
                        <div>
                            <label className={labelStyle}>Resume <span className="text-red-500">*</span></label>
                            <p className="text-gray-500 text-sm mb-3">
                                Upload your resume (PDF, DOC, or DOCX, max 10MB)
                            </p>
                            <div className="flex items-center gap-3">
                                <input
                                    type="file"
                                    name="resume"
                                    onChange={(e) => handleFileChange(e, setResumeFileName)}
                                    accept=".pdf,.doc,.docx"
                                    className="hidden"
                                    id="resume-input"
                                />
                                <button
                                    type="button"
                                    onClick={() => document.getElementById('resume-input').click()}
                                    className="flex items-center gap-2 px-4 py-2 border-2 border-[#5BB5B0] text-[#008080] rounded-lg hover:bg-teal-50 transition-colors"
                                >
                                    <Upload size={18} />
                                    Choose File
                                </button>
                                {resumeFileName && <span className="text-gray-600 text-sm">✓ {resumeFileName}</span>}
                            </div>
                        </div>

                        {/* Cover Letter Upload */}
                        <div>
                            <label className={labelStyle}>Cover Letter <span className="text-gray-400 text-sm">(Optional)</span></label>
                            <div className="flex items-center gap-3">
                                <input
                                    type="file"
                                    name="cover_letter"
                                    onChange={(e) => handleFileChange(e, setCoverLetterFileName)}
                                    accept=".pdf,.doc,.docx"
                                    className="hidden"
                                    id="cover-letter-input"
                                />
                                <button
                                    type="button"
                                    onClick={() => document.getElementById('cover-letter-input').click()}
                                    className="flex items-center gap-2 px-4 py-2 border-2 border-[#5BB5B0] text-[#008080] rounded-lg hover:bg-teal-50 transition-colors"
                                >
                                    <Upload size={18} />
                                    Choose File
                                </button>
                                {coverLetterFileName && <span className="text-gray-600 text-sm">✓ {coverLetterFileName}</span>}
                            </div>
                        </div>

                        {/* Work Rights Confirmation */}
                        <div className="flex items-start gap-3 pt-4">
                            <input
                                type="checkbox"
                                name="confirm_work_rights"
                                className="mt-1 w-5 h-5 accent-[#008080]"
                                required
                            />
                            <label className="text-gray-600 text-sm leading-relaxed">
                                I confirm that I have the right to work in Australia and am willing to undergo
                                all required screening checks (NDIS Worker Screening, Police Check, WWCC) as
                                part of the employment process.
                            </label>
                        </div>

                        {/* reCAPTCHA */}
                        <div className="flex justify-center pt-4">
                            <ReCAPTCHA
                                key="careers-recaptcha"
                                ref={recaptchaRef}
                                sitekey={RECAPTCHA_SITE_KEY}
                                onChange={(token) => setCaptchaToken(token)}
                                onExpired={() => setCaptchaToken(null)}
                            />
                        </div>

                        {/* Submit Button */}
                        <div className="text-center pt-8">
                            <button
                                type="submit"
                                disabled={status === 'sending'}
                                className="bg-white border-2 border-[#5BB5B0] text-[#008080] font-bold py-3 px-16 rounded-full hover:bg-teal-50 transition-all duration-300 disabled:opacity-50 uppercase tracking-widest text-lg"
                            >
                                {status === 'sending' ? 'Submitting...' : 'SUBMIT'}
                            </button>
                        </div>

                        {status === 'error' && (
                            <div className="text-center text-red-600 font-medium">
                                Failed to submit application. Please try again.
                            </div>
                        )}
                    </form>
                </div>
            </section>
        </div>
    );
};

export default CareersPage;
